import { TLHanaArtifactSuffix } from '@/api/gen/tessera-backend-api.schemas';

export const COOKIES = {
  TENANT: {
    ID: 'tlai-tenant-id',
  },
  AUTH: {
    ACCESS_TOKEN: 'tlai-access-token',
    REFRESH_TOKEN: 'tlai-refresh-token',
    EXPIRES_AT: 'tlai-expires-at',
    EXPIRES_IN: 'tlai-expires-in',
  },
  EXPERIENCE: {
    HAS_VISITED: 'tlai-experience-has-visited',
    LAST_SELECTED_PROGRAM_ID: 'tlai-experience-last-selected-program-id',
    LAST_VISITED_CONVERSATION_ID: 'tlai-experience-last-visited-conversation-id',
  },
} as const;

export const REQUEST_HEADERS = {
  AUTHORIZATION: 'Authorization',
  REFRESH_TOKEN: 'X-Refresh-Token',
  TENANT_ID: 'X-Tenant-ID',
  SKIP_PROXY: 'X-Skip-Proxy',
} as const;

export const REFRESH_TOKEN_THRESHOLD = 60;
export const REFRESH_TOKEN_THRESHOLD_MS = REFRESH_TOKEN_THRESHOLD * 1000;

export const TLAI_COOKIES = {
  TENANT: {
    ID: 'tlai-tenant-id',
  },
  AUTH: {
    ACCESS_TOKEN: 'tlai-access-token',
    REFRESH_TOKEN: 'tlai-refresh-token',
    EXPIRES_AT: 'tlai-expires-at',
    EXPIRES_IN: 'tlai-expires-in',
  },
  EXPERIENCE: {
    HAS_VISITED: 'tlai-experience-has-visited',
    LAST_SELECTED_PROGRAM_ID: 'tlai-experience-last-selected-program-id',
    LAST_VISITED_CONVERSATION_ID: 'tlai-experience-last-visited-conversation-id',
  },
} as const;

export const TLAI_FEATURE_FLAGS = {
  FLE_REMEDIATION_ENABLED: 'FLE_REMEDIATION_ENABLED',
  MAX_OBJECT_IDS_PER_REQUEST: 'MAX_OBJECT_IDS_PER_REQUEST',
  MAX_FINDINGS_OBJECT_IDS_PER_REQUEST: 'MAX_FINDINGS_OBJECT_IDS_PER_REQUEST',
  REMEDY_SKIP_COMMENTING_RULES: 'REMEDY_SKIP_COMMENTING_RULES',
  REMEDY_ISSUES_ENABLED: 'REMEDY_ISSUES_ENABLED',
  DEFAULT_REMEDY_CHECK_VARIANT: 'DEFAULT_REMEDY_CHECK_VARIANT',
  DATA_HARMONIZATION_UAT_2: 'DATA_HARMONIZATION_UAT_2',
  DATA_HARMONIZATION_VIEWS: 'DATA_HARMONIZATION_VIEWS',
  CLOUD_STORAGE_DIRECT_UPLOAD_ENABLED: 'CLOUD_STORAGE_DIRECT_UPLOAD_ENABLED',
  SHOW_INTERMEDIATE_STEPS_PANEL: 'SHOW_INTERMEDIATE_STEPS_PANEL',
  SHOW_SYSTEMS: 'SHOW_SYSTEMS',
  NEW_NAVIGATION: 'NEW_NAVIGATION',
  SHOW_TOP_BAR: 'SHOW_TOP_BAR',
} as const;

export const TLAI_REQUEST_HEADERS = {
  TENANT_ID: 'X-Tenant-ID',
} as const;

export const TLAI_SESSION_STORAGE = {
  LOOM_IMPORT: 'loom_import_data',
  TEMPLATE_IMPORT: 'template_import_data',
  /** Carried-over summary for "Summarize & start new chat" — tab-scoped hand-off until first send. */
  NEW_CHAT_PENDING_SUMMARY: 'tlai-new-chat-pending-summary',
  /** Conversation whose memory is being cloned into this new chat — paired with `NEW_CHAT_PENDING_SUMMARY`. */
  NEW_CHAT_SCRATCHPAD_SOURCE_CONVERSATION_ID: 'tlai-new-chat-scratchpad-source-conversation-id',
} as const;

export const TLAI_LOCAL_STORAGE = {
  NAVIGATION: {
    PANEL_COLLAPSED: 'tlai-navigation-panel-collapsed',
  },
  WORKBENCH: {
    RECENT_PROJECTS: 'tlai-workbench-recent-projects',
  },
  EDITOR: {
    TABS: 'tlai-editor-tabs',
    ACTIVE_TAB: 'tlai-editor-active-tab',
  },
  CONVERSATION: {
    ATTACHMENTS_DRAFT_PREFIX: 'conversation-attachments-draft:',
    MESSAGE_DRAFT_PREFIX: 'conversation-message-draft:',
    LAST_CONVERSATION_PREFIX: 'conversation-last:',
  },
  REMEDY: {
    FORMAT_ON_SAVE: 'remedy-format-on-save',
    COMMENT_TEMPLATES: 'remedy-comment-templates',
  },
  LOOM: {
    PANEL_WIDTH_NODE: 'tlai-loom-panel-width-node',
    PANEL_WIDTH_TOOLBOX: 'tlai-loom-panel-width-toolbox',
    PANEL_WIDTH_EXECUTION: 'tlai-loom-panel-width-execution',
    PANEL_WIDTH_EDGE: 'tlai-loom-panel-width-edge',
  },
  SKILLS_HUB: {
    MAIN_SPLIT_LAYOUT: 'tlai-skills-hub-main-split-layout',
  },
  KNOWLEDGE_BASE: {
    WIKI_GRAPH_SHOW_LABELS: 'kb-wiki-graph-show-labels',
    WIKI_GRAPH_VIEW_MODE: 'kb-wiki-graph-view-mode',
    WIKI_GRAPH_COLOR_MODE: 'kb-wiki-graph-color-mode',
    WIKI_GRAPH_WITH_DOCUMENTS: 'kb-wiki-graph-with-documents',
    WIKI_OVERVIEW_GRAPH_WITH_DOCUMENTS: 'kb-wiki-overview-graph-with-documents',
  },
  WORKLIST: {
    VIEW_MODE: 'tlai-worklist-view-mode',
  },
  DATA_HARMONIZATION: {
    UNIVERSE_GRAPH_GROUP_BY_WORKSTREAM: 'dh-universe-graph-group-by-workstream',
    UNIVERSE_GRAPH_SHOW_CHECKS: 'dh-universe-graph-show-checks',
    UNIVERSE_GRAPH_COLOR_MODE: 'dh-universe-graph-color-mode',
  },
  DOCUMENTATION: {
    SIDEBAR_COLLAPSED: 'tlai-docs-sidebar-collapsed',
  },
} as const;

/**
 * Reviewer-scoped persisted UI keys. Kept separate from `EDITOR_STORAGE_KEYS`
 * so the reviewer's storage lifecycle (filters) doesn't accidentally get
 * cleared by editor-namespace tooling. Tenant-scoped via `reviewerStorageKey()`
 * in `reviewer/reviewer-persistence.ts`.
 */
export const REVIEWER_STORAGE_KEYS = {
  /** Last filter set the reviewer applied — restored on load. */
  FILTERS: 'reviewer-filters',
} as const;

export const EDITOR_STORAGE_KEYS = {
  TABS: 'tabs',
  ACTIVE_TAB: 'active-tab',
  LAST_SELECTED_PROGRAM: 'last-selected-program',
  CONTEXT_PANEL_WIDTH: 'context-panel-width',
  CONTEXT_PANEL_COLLAPSED: 'context-panel-collapsed',
  /** Landscape-sync DiscoverPanel: recent source/target package values
   * for the autofill datalist. Most-recent-first, deduped, capped at 10. */
  LANDSCAPE_SYNC_RECENT_PACKAGES: 'landscape-sync-recent-packages',
  /** Ordered array of favorited conversation_ids in the chat history dropdown. */
  CHAT_FAVORITES: 'chat-favorites',
  /** Whether the My Programs / Programs section in EditorLibrary is collapsed. */
  PROGRAMS_SECTION_COLLAPSED: 'programs-section-collapsed',
  /** Whether the SAP Repository section in EditorLibrary is collapsed. */
  SAP_SECTION_COLLAPSED: 'sap-section-collapsed',
  /** Client-side workflow graph node positions keyed by task id. */
  WORKFLOW_NODE_POSITIONS: 'workflow-node-positions',
  /** Plexus checkpoint: last chosen target system / target tables, suffixed with project id. */
  NEXUS_PLEXUS_TARGET: 'nexus-plexus-target',
  /** Plexus propose step: last chosen matching parallelism, suffixed with project id. */
  NEXUS_PLEXUS_MATCH_CONCURRENCY: 'nexus-plexus-match-concurrency',
  /** Nexus workbench: pinned/highlighted field node ids per blueprint, suffixed with project id. */
  NEXUS_PINNED_FIELDS: 'nexus-pinned-fields',
  /** Nexus Fields grid: draggable column order per blueprint, suffixed with project id. */
  NEXUS_FIELDS_COLUMN_ORDER: 'nexus-fields-column-order',

  /** One-shot prefill for the editor chat composer, written when navigating
   * to the editor from elsewhere (e.g. "Try in chat" in the editor Skills Hub). */
  CHAT_MESSAGE_DRAFT: 'editor-chat-message-draft',
  /** Whether the code-remedy floating chat is open. */
  REMEDY_CHAT_OPEN: 'remedy-chat-open',
  /** Last-selected ownership filter (all/owned/shared) on the loom list tabs. */
  OWNERSHIP_FILTER: 'ownership-filter',
  /** Last-selected page size for the DH graph-setup wizard's Fields table. */
  DH_GRAPH_FIELDS_PAGE_SIZE: 'dh-graph-fields-page-size',
} as const;

export const DEFAULT_TENANT_ID = 'default';

export const APP_SESSION_COOKIE_NAME = 'tessera-app-session';

export const DEV_TOOL_OVERRIDES_COOKIE_NAME = 'tlai-dev-tool-overrides';

/** Pre-rename name of {@link DEV_TOOL_OVERRIDES_COOKIE_NAME}. It is httpOnly with a 400-day
 * max-age, so browsers that still hold it need an explicit clear from the server. */
export const LEGACY_DEV_TOOL_OVERRIDES_COOKIE_NAME = 'tlai-feature-flag-overrides';

export const MIN_ACCEPTABLE_DEV_TOOL_OVERRIDES_VERSION = '0.0.0';

export const MAX_COOKIE_AGE = 60 * 60 * 24 * 400; // 400 days -- This is a browser limit, not anything specific to us.

// File upload constants
const CONVERSATION_EXTENSIONS = [
  '.txt',
  '.csv',
  '.xls',
  '.xlsx',
  '.pdf',
  '.md',
  '.rtf',
  '.doc',
  '.docx',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.svg',
  '.json',
  '.parquet',
] as const;

const CONVERSATION_MIME_TYPES = [
  'text/plain',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/pdf',
  'text/markdown',
  'text/rtf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/json',
  'application/octet-stream',
] as const;

const MIGRATION_EXTENSIONS = ['.md', '.abap', '.csv', '.parquet', '.db', '.duckdb'] as const;
const MIGRATION_ACCEPT = MIGRATION_EXTENSIONS.join(',');

const EDITOR_EXTENSIONS = ['.abap', '.md', '.csv', '.py', '.parquet', '.db', '.duckdb'] as const;
const EDITOR_ACCEPT = EDITOR_EXTENSIONS.join(',');

const DATA_CHECK_EXTENSIONS = ['.json', '.csv', '.tsv', '.xlsx'] as const;
const DATA_CHECK_ACCEPT = DATA_CHECK_EXTENSIONS.join(',');

const CODE_REMEDY_EXTENSIONS = ['.xlsx', '.csv'] as const;
const CODE_REMEDY_ACCEPT = CODE_REMEDY_EXTENSIONS.join(',');

const VIEW_REMEDIATION_EXTENSIONS = ['.xml', ...Object.values(TLHanaArtifactSuffix).map((suffix) => `.${suffix}`)];
const VIEW_REMEDIATION_ACCEPT = [...VIEW_REMEDIATION_EXTENSIONS, 'text/xml', 'application/xml'].join(',');

// View Remediation setup spreadsheet (rows of name/type/package to bulk-import).
const VIEW_REMEDIATION_SETUP_EXTENSIONS = ['.csv', '.xlsx'] as const;
const VIEW_REMEDIATION_SETUP_ACCEPT = [
  ...VIEW_REMEDIATION_SETUP_EXTENSIONS,
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');

// Bulk remediated upload: a .zip of remediated view files + a .csv/.xlsx mapping.
const VIEW_REMEDIATION_BULK_ZIP_EXTENSIONS = ['.zip'] as const;
const VIEW_REMEDIATION_BULK_ZIP_ACCEPT = [
  ...VIEW_REMEDIATION_BULK_ZIP_EXTENSIONS,
  'application/zip',
  'application/x-zip-compressed',
].join(',');
// Mapping file shares the setup spreadsheet's extensions (.csv/.xlsx).
const VIEW_REMEDIATION_BULK_MAPPING_ACCEPT = VIEW_REMEDIATION_SETUP_ACCEPT;
// Server-enforced caps (mirror the backend); pre-checked client-side to avoid a doomed upload.
const VIEW_REMEDIATION_BULK_ZIP_MAX_BYTES = 100 * 1024 * 1024;
const VIEW_REMEDIATION_BULK_MAPPING_MAX_BYTES = 10 * 1024 * 1024;

export const FILE_UPLOAD = {
  // Conversation attachments
  CONVERSATION_EXTENSIONS,
  CONVERSATION_ACCEPT: [...CONVERSATION_EXTENSIONS, ...CONVERSATION_MIME_TYPES].join(','),

  // Migration files
  MIGRATION_EXTENSIONS,
  MIGRATION_ACCEPT,

  // Editor files
  EDITOR_EXTENSIONS,
  EDITOR_ACCEPT,

  // Data Check files (code files for validation)
  DATA_CHECK_EXTENSIONS,
  DATA_CHECK_ACCEPT,

  // Code Remediation files
  CODE_REMEDY_EXTENSIONS,
  CODE_REMEDY_ACCEPT,

  // View Remediation files (HANA calculation/analytic/attribute views)
  VIEW_REMEDIATION_EXTENSIONS,
  VIEW_REMEDIATION_ACCEPT,

  // View Remediation setup spreadsheet
  VIEW_REMEDIATION_SETUP_EXTENSIONS,
  VIEW_REMEDIATION_SETUP_ACCEPT,

  // View Remediation bulk remediated upload (zip of views + mapping file)
  VIEW_REMEDIATION_BULK_ZIP_EXTENSIONS,
  VIEW_REMEDIATION_BULK_ZIP_ACCEPT,
  VIEW_REMEDIATION_BULK_MAPPING_ACCEPT,
  VIEW_REMEDIATION_BULK_ZIP_MAX_BYTES,
  VIEW_REMEDIATION_BULK_MAPPING_MAX_BYTES,
} as const;

export const CONVERSATION_LIMITS = {
  // The conversation limit (max auto-compactions before prompting a fresh chat) is owned and
  // enforced by the backend; the dialog only reacts to the COMPACTION_LIMIT_REACHED error code,
  // so no frontend constant is needed. There is no file-size cap — only a count cap.
  /**
   * Default maximum number of files a conversation can hold in total (cumulative across every turn,
   * not per message). Overridable per-tenant via the `conversation_max_attachments` tenant setting;
   * this value is the fallback used when the tenant hasn't configured one. Mirrors the backend default.
   */
  MAX_ATTACHMENTS_PER_CONVERSATION: 100,
  /**
   * Inclusive range an admin may configure for the per-tenant `conversation_max_attachments` cap.
   * The runtime clamps to this range and the tenant editor rejects out-of-range saves, so the stored
   * value always matches the effective one. Mirrors the backend clamp (`resolve_attachment_cap`).
   */
  ATTACHMENT_CAP_MIN: 1,
  ATTACHMENT_CAP_MAX: 200,
} as const;

/**
 * The signature to use for commenting out code in the code remediation feature.
 * TBD if this should be a tenant-configurable setting.
 */
export const COMMENT_SIGNATURE = 'REL- <REL NR> Tessera AI';

/**
 * ADT URI suffixes that address source code rather than the object itself.
 * Mirrors the backend constants of the same name in
 * `src/constants/sap_agent/adt.py`. Strip these to normalize a source-level
 * ADT URI back to the object-level form.
 */
export const SAP_ADT_SOURCE_MAIN_SUFFIX = '/source/main';
export const SAP_ADT_SOURCE_INACTIVE_SUFFIX = '/source/inactive';

export const LARGE_EXPORT_STARTED_MESSAGE =
  "Large export started — you'll get a notification when it's ready to download";
